<template>
  <div :class="classList" class="edrone-card" @click="$emit('cardClick')">
    <div v-if="hasHeaderSlot" class="edrone-card-header">
      <slot name="header" />
    </div>
    <div class="edrone-card-body">
      <slot />
    </div>
    <div v-if="hasFooterSlot" class="edrone-card-footer">
      <slot name="footer" />
    </div>
    <div v-if="hasOverlaySlot" class="edrone-card-overlay">
      <slot name="overlay" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";

export default defineComponent({
  name: "EdroneCard",
  props: {
    inline: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
    active: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
    cardMd: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
    cardSm: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
    bodyScrollable: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
    footerVisible: {
      default: true,
      type: Boolean as PropType<boolean>,
    },
    fullStretch: {
      required: false,
      default: false,
      type: Boolean as PropType<boolean>,
    },
  },
  emits: ["cardClick"],
  computed: {
    classList(): Record<string, boolean> {
      return {
        "edrone-card--inline": this.inline,
        "edrone-card--active": this.active,
        "edrone-card--size-md": this.cardMd,
        "edrone-card--size-sm": this.cardSm,
        "edrone-card--body-scrollable": this.bodyScrollable,
        "edrone-card--full-stretch": this.fullStretch,
      };
    },
    hasHeaderSlot(): boolean {
      return !!this.$slots.header;
    },
    hasFooterSlot(): boolean {
      return !!this.$slots.footer && this.footerVisible;
    },
    hasOverlaySlot(): boolean {
      return !!this.$slots.overlay;
    },
  },
});
</script>

<style scoped>
.edrone-card {
  border-radius: 20px;
  background: white;
  box-shadow: 0 0 15px #00000014;
  position: relative;
}

.edrone-card.edrone-card--active {
  box-shadow: 0 20px 24px 4px rgb(0 0 0 / 10%), 0 1px 10px -2px rgb(0 0 0 / 40%);
}

.edrone-card-body {
  position: relative;
  padding: 32px;
}

.edrone-card--size-md .edrone-card-body {
  padding: 20px;
}

.edrone-card--size-sm .edrone-card-body {
  padding: 15px;
}

.edrone-card--body-scrollable > .edrone-card-body {
  max-height: 555px;
  overflow-x: hidden;
  overflow-y: scroll;
  padding-top: 2px;
  padding-right: 22px;
}

.edrone-card--body-scrollable > .edrone-card-footer {
  background-color: #fff;
  padding: 35px 0 0;
}

.edrone-card--body-scrollable > .edrone-card-body::-webkit-scrollbar {
  width: 10px;
}

.edrone-card--body-scrollable > .edrone-card-body::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px #eee;
}

.edrone-card--body-scrollable > .edrone-card-body::-webkit-scrollbar-thumb {
  background: #ccc 0 0 no-repeat padding-box;
  box-shadow: 0 0 5px #0000001a;
  border-radius: 5px;
}

.edrone-card-header {
  padding: 30px 30px 0 30px;
}

.edrone-card--size-md .edrone-card-header {
  padding: 20px 20px 0 20px;
}

.edrone-card--size-sm .edrone-card-header {
  padding: 15px 20px 0 20px;
}

.edrone-card-footer {
  padding: 30px;
}

.edrone-card--size-md .edrone-card-footer {
  padding: 20px;
}

.edrone-card--size-sm .edrone-card-footer {
  padding: 15px;
}

.edrone-card-header h1,
.edrone-card-header h2,
.edrone-card-header h3,
.edrone-card-header h4,
.edrone-card-header h5,
.edrone-card-header h6,
.edrone-card-header p,
.edrone-card-header span {
  font-size: 30px;
  margin: 0;
  font-weight: 400;
}

.edrone-card-footer {
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  background: #fafafa;
}

.edrone-card--inline {
  display: inline-block;
}

.edrone-card--yellow .edrone-card-footer {
  background: #f2f200 !important;
}

.edrone-card-overlay {
  opacity: 0;
  transition-duration: 0.3s;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 20px;
}

.edrone-card:hover .edrone-card-overlay {
  opacity: 1;
  transition-duration: 0.3s;
}

.edrone-card--full-stretch {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.edrone-card--full-stretch .edrone-card-header {
  height: 100%;
}
</style>
